import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import { PhArrowRight, PhChartBar, PhStar, PhTable } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import IconBadge from 'tio-ui/components/icon-badge';
import type { ComponentLike } from '@glint/template';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';

export type RowIcon = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;

/** Descriptions past this length are ellipsised so a long one cannot push the row's controls off. */
const MAX_DESCRIPTION_LENGTH = 120;

interface PartnerReportRowSignature {
  Args: {
    /** Supplies the row's name, description and updated time. */
    report: Report;
    /** Route the name link and the Open button both target. */
    route: string;
    /** Positional models for @route, when it takes dynamic segments. */
    models?: unknown[];
    /** Query params for @route. */
    query?: Record<string, unknown>;
    /** Leading badge icon. Reports default to a table; dashboards and legacy rows pass their own. */
    icon?: RowIcon;
    /** Renders the "Retiring" badge — legacy QuickSight rows. */
    isRetiring?: boolean;
    /** Opens the usage-stats modal. Omit to hide the stats affordance entirely. */
    onStats?: (report: Report) => void;
    /** Drives the star's treatment and whether the control reads as pin or unpin. */
    isPinned?: boolean;
    /** Omit to hide the pin toggle entirely. */
    onTogglePin?: (report: Report) => void;
  };
  Element: HTMLDivElement;
}

/**
 * The one row the partner Reports page uses for dashboards, detailed reports and legacy
 * QuickSight entries alike. Distinct from the admin `reports/report-row`, which is a dense
 * table row that links into the builder and carries archive actions.
 */
export default class PartnerReportRow extends Component<PartnerReportRowSignature> {
  get icon() {
    return this.args.icon ?? PhTable;
  }

  get models() {
    return this.args.models ?? [];
  }

  get query() {
    return this.args.query ?? {};
  }

  /** '' when the report has no description, so the template can skip the paragraph. */
  get description() {
    const description = this.args.report.description;
    if (!description) return '';
    if (description.length <= MAX_DESCRIPTION_LENGTH) return description;
    return `${description.slice(0, MAX_DESCRIPTION_LENGTH)}…`;
  }

  openStats = () => this.args.onStats?.(this.args.report);

  togglePin = () => this.args.onTogglePin?.(this.args.report);

  <template>
    <Card
      @radius="nested"
      @elevation="none"
      @padding="none"
      class="tio-row tio-elevation-card-hover flex-wrap px-4 py-3 hover:tio-border-brand"
      data-test-partner-report-row={{@report.id}}
      ...attributes
    >
      <IconBadge @icon={{this.icon}} @color="purple" @size="sm" class="shrink-0" />

      <div class="tio-col-extra-tight min-w-0 flex-1">
        <div class="tio-row-tight flex-wrap">
          <LinkTo
            class="tio-text-sm-semibold tio-fg-primary hover:tio-fg-brand truncate"
            @route={{@route}}
            @models={{this.models}}
            @query={{this.query}}
            data-test-partner-report-link
          >{{@report.name}}</LinkTo>
          {{#if @isRetiring}}
            <span
              class="tio-text-xs-bold rounded-full border tio-border-warning tio-bg-warning-soft px-1.5 py-1 uppercase tio-fg-warning"
              data-test-partner-report-retiring
            >{{t "reporting.retiring"}}</span>
          {{/if}}
        </div>

        {{#if this.description}}
          <p class="tio-text-xs-normal tio-fg-secondary text-pretty">{{this.description}}</p>
        {{/if}}

        <div class="tio-row-loose tio-text-xs-normal flex-wrap tio-fg-muted">
          <span class="whitespace-nowrap">{{t
              "reporting.updated"
              date=@report.formattedUpdatedAt
            }}</span>
          {{#if @onStats}}
            <Button
              @appearance="minimal"
              @size="sm"
              @icon={{PhChartBar}}
              @label={{t "reporting.views"}}
              @ariaLabel="{{t 'reporting.views'}} — {{@report.name}}"
              data-test-partner-report-stats
              {{on "click" this.openStats}}
            />
          {{/if}}
        </div>
      </div>

      <div class="tio-row-tight shrink-0">
        {{#if @onTogglePin}}
          <Button
            @appearance="minimal"
            @size="sm"
            @intent={{if @isPinned "warning" "default"}}
            @icon={{PhStar}}
            @ariaLabel="{{if
              @isPinned
              (t 'reporting.unpin_report')
              (t 'reporting.pin_report')
            }} — {{@report.name}}"
            aria-pressed="{{if @isPinned 'true' 'false'}}"
            data-test-partner-report-pin
            {{on "click" this.togglePin}}
          />
        {{/if}}

        <LinkTo
          class="tio-inline-row-extra-tight tio-text-xs-semibold whitespace-nowrap rounded-lg border tio-border-strong tio-bg-surface px-3 py-1.5 tio-fg-primary hover:tio-bg-sunken"
          @route={{@route}}
          @models={{this.models}}
          @query={{this.query}}
          data-test-partner-report-open
        >
          {{t "reporting.open"}}
          <PhArrowRight class="h-3 w-3" />
        </LinkTo>
      </div>
    </Card>
  </template>
}
